import {Spec} from 'immutability-helper';
import React from 'react';

import {setURLParams} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import noteLinkedEmbeddingPlugin from 'notebook/embeddings/NoteLinkedEmbeddingPlugin';
import {
  ChartNotePart,
  GenericNoteLinkedChart,
  NoteLinkedChart,
} from 'notebook/integrations/charts/NoteLinkedChartTypes';
import * as NoteLinkedChartUtil from 'notebook/integrations/charts/NoteLinkedChartUtil';
import NoteLinkedChartView from 'notebook/integrations/charts/NoteLinkedChartView';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor} from 'notebook/notes/NoteEditorTypes';

export type {ChartNotePart, GenericNoteLinkedChart, NoteLinkedChart};
const MEDIOCRE_FILE_ID_ATTR = 'data-mediocre-file-id';

function shouldShowAnalysisProvenance(notePart: ChartNotePart): boolean {
  // This is for backcompat, but charts didn't use to have a type field, so we just assume they're analysis in that case.
  return (
    Boolean(notePart.item.provenance?.url) &&
    (notePart.item.provenance?.type === undefined || notePart.item.provenance?.type === 'analysis')
  );
}

function getRenderableChartId(notePart: ChartNotePart, editor: NoteEditor): string {
  return String(
    (notePart.item.chart.cid ? editor.props.getIdFromCid(notePart.item.chart.cid) : undefined) ??
      notePart.item.chart.id ??
      notePart.item.chart.source_id ??
      ''
  );
}

function getClipboardSourceFileId($el: JQuery): string | undefined {
  return $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) ?? $el.attr(MEDIOCRE_FILE_ID_ATTR);
}

const NOTE_LINKED_CHART_PLUGIN = noteLinkedEmbeddingPlugin<ChartNotePart>({
  type: ModelTypeConstants.NOTE_LINKED_CHART,
  title: 'Chart',
  icon: 'line-chart',
  getContentId: (notePart: ChartNotePart) => notePart.item.chart.id ?? notePart.item.chart.source_id ?? '',
  getEmbedding: (notePart: ChartNotePart, editor: NoteEditor) => (
    <NoteLinkedChartView chartId={getRenderableChartId(notePart, editor)} />
  ),
  getName: (notePart: ChartNotePart) => notePart.item.name,
  getNameUpdate: (notePart: ChartNotePart, name: string) => ({name}),
  getProvenance: (notePart: ChartNotePart) =>
    shouldShowAnalysisProvenance(notePart)
      ? {
          url: setURLParams(notePart.item.provenance.url, {
            screen: 'view',
            chartId: notePart.item.chart.id ?? notePart.item.chart.source_id,
          }),
          type: 'Analysis',
          icon: 'analytics_analysis',
        }
      : undefined,
  itemToHTML(this: NoteEditor, $el: JQuery, item: ChartNotePart): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },
  itemFromHTML(this: NoteEditor, $el: JQuery, item: ChartNotePart): ChartNotePart | null {
    const sourceFileId = getClipboardSourceFileId($el);
    return {
      ...item,
      item: NoteLinkedChartUtil.copyNoteLinkedChartForPaste({
        source: item.item,
        shouldCloneChart:
          // Currently only chat charts are cloned when pasted in the same document.
          // This is intended to support analysis charts linking directly to the original chart in the analysis
          item.item.provenance?.type === 'chat' ||
          (sourceFileId !== undefined && sourceFileId !== this.props.fileId) ||
          (item.item.chart.id == null &&
            (item.item.chart.cid == null || this.props.getIdFromCid(item.item.chart.cid) == null)),
      }),
    };
  },
  convertItemForPaste(item: ChartNotePart): {
    addedItem: ChartNotePart;
    oldName: string;
    newName: string;
  } {
    return NoteLinkedChartUtil.convertItemForPaste(item);
  },
  setContent(this: NoteEditor, data: unknown): Spec<ChartNotePart> {
    const {item, text, formats} = data as Partial<ChartNotePart>;
    const updates: Partial<ChartNotePart> = {};
    if (item !== undefined) {
      updates.item = item;
    }
    if (text !== undefined) {
      updates.text = text;
    }
    if (formats !== undefined) {
      updates.formats = formats;
    }
    return {$merge: updates} as Spec<ChartNotePart>;
  },
  prepareForStore: NoteLinkedChartUtil.prepareForStore,
  prepareForSave: NoteLinkedChartUtil.prepareForSave,
});

export default NOTE_LINKED_CHART_PLUGIN;
